import React from 'react';
import ReactDOM from 'react-dom/client';
import html2canvas from 'html2canvas';
import styles from './DownloadLabelView.module.scss';
import { ShippingConvenienceLabel } from '@/services/item';
import { buildImageUrl } from '@/utilities/image';
import { Item } from '@/types/item';

type DownloadableElements = {
  root: ReactDOM.Root;
  view: HTMLDivElement;
};
function appendElementToBody(element: React.ReactElement): DownloadableElements {
  const view = document.createElement('div');
  view.id = `downloadable-view-${Date.now()}`;
  view.style.position = 'fixed';
  view.style.top = '0';
  view.style.left = '-9999px';
  const root = ReactDOM.createRoot(view);
  root.render(element);
  document.body.appendChild(view);

  return { root, view };
}

function downloadAndCleanUp({ root, view }: DownloadableElements, fileName: string, urlData: string): void {
  const link = document.createElement('a');
  link.href = urlData;
  link.download = fileName;
  link.click();
  root.unmount();
  view.remove();
}

async function getQrUrlData(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(`${reader.result}`);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function renderAndDownload(
  element: React.ReactElement,
  fileName: string,
  qrCodeUrl: string,
): Promise<void> {
  const downloadableElements = appendElementToBody(element);

  const recover = () =>
    getQrUrlData(qrCodeUrl).then((data) => downloadAndCleanUp(downloadableElements, fileName, data));

  const node = document.getElementById(downloadableElements.view.id);
  if (node == null) {
    return recover();
  }
  // workaround: 500ms waiting for react dom to render
  return new Promise((rs) => setTimeout(rs, 500))
    .then(() => html2canvas(node, { logging: false, useCORS: true }))
    .then((canvas) => canvas.toDataURL('image/jpeg', 1))
    .then((dataUrl) => downloadAndCleanUp(downloadableElements, fileName, dataUrl))
    .catch(() => recover());
}

export const DownloadView: React.FC<{
  shipmentDetails: ShippingConvenienceLabel;
  product: Item;
  shipByLabel: string;
  trackingLabel: string;
  refLabel: string;
}> = ({ shipmentDetails, product, shipByLabel, trackingLabel, refLabel }) => {
  const { chosenCarrier, shipByDate, documentUrl, trackingReference } = shipmentDetails;
  const { name, iconUrl } = chosenCarrier || {};

  return (
    <div className={styles[`downloadCodeView`]}>
      <div className={styles[`downloadCodeView--inner`]}>
        <div className={styles[`downloadCodeView__product`]}>
          <img
            className={styles[`downloadCodeView__product--image`]}
            src={buildImageUrl(product.imageUrl || '')}
            alt={product.productName || ''}
            // onError={onImageError}
          />
          <div className={styles[`downloadCodeView__product--info`]}>
            <div className={styles[`downloadCodeView__product--info--name`]}>{product.brandName}</div>
            <div className={styles[`downloadCodeView__product--info--type`]}>{product.productName}</div>
            <div>
              {refLabel}. {product.productId}
            </div>
          </div>
        </div>
        <div className={styles[`downloadCodeView__shipdate`]}>
          <div className={styles[`downloadCodeView__shipdate--label`]}>{shipByLabel}</div>
          <div className={styles[`downloadCodeView__shipdate--value`]}>{shipByDate}</div>
        </div>
        <div className={styles[`downloadCodeView__shipdate`]}>
          <img
            alt={name}
            src={iconUrl || ''}
            className={styles[`downloadCodeView__shipdate--image`]}
          />
          <div className={styles[`downloadCodeView__shipdate--value`]}>{name}</div>
        </div>
        <div className={styles[`downloadCodeView__shipdate`]}>
          <div className={styles[`downloadCodeView__shipdate--label`]}>{trackingLabel}:</div>
          <div className={styles[`downloadCodeView__shipdate--value`]}>{trackingReference}</div>
        </div>

        <div className={styles[`downloadCodeView__qr`]}>
          <img
            src={documentUrl || ''}
            alt="QR Code"
            className={styles[`downloadCodeView__qr--image`]}
          />
        </div>
      </div>
    </div>
  );
};
